import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import { useContent } from '../hooks/useContent';
import { useSubscription } from '../hooks/useSubscription';
import PageTemplate from './PageTemplate';
import ProfileMenuBar from './ProfileMenuBar';
import { UnifiedHeader } from './UnifiedHeader';
import AccountTab from './AccountTab';
import UserStatsTab from './UserStatsTab';
import SystemStatsTab from './SystemStatsTab';
import AdminTab from './AdminTab';
import UsersTab from './UsersTab';
import QueueTab from './QueueTab';
import TrashView from './TrashView';
import SubscriptionTab from './SubscriptionTab';
import '../styles/components/profile.css';

export type ProfileTab = 'account' | 'subscription' | 'trash' | 'admin' | 'users' | 'stats' | 'queue';
type FilterType = 'all' | 'today' | 'yesterday' | 'last_delete';

interface ProfileViewProps {
  isActive?: boolean;
  onNavigateToCollection?: (collectionId: number) => void;
  onTrashCountChange?: (total: number, filtered: number, selected: number) => void;
  onViewPlans?: () => void;
  onFontSizeToggle?: () => void;
  fontSize?: string;
  onShowQuickStartGuide?: () => void;
  onOpenAppContext?: () => void;
}

const ProfileView: React.FC<ProfileViewProps> = ({ 
  isActive = true, 
  onNavigateToCollection, 
  onTrashCountChange, 
  onViewPlans, 
  onFontSizeToggle, 
  fontSize, 
  onShowQuickStartGuide,
  onOpenAppContext
}) => {
  const { user } = useAuth();
  const { activeSpace } = useSpace();
  const { profile } = useContent();
  const { hasSubscriptionIssue } = useSubscription();
  const [activeTab, setActiveTab] = useState<ProfileTab>(() => {
    const stored = localStorage.getItem('profile-activeTab') as ProfileTab | null;
    return stored || 'account';
  });

  // Trash state - lifted from TrashView
  const [trashFilter, setTrashFilter] = useState<FilterType>('all');
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [trashTotalCount, setTrashTotalCount] = useState(0);
  const [trashRestoring, setTrashRestoring] = useState(false);
  const [trashEmptying, setTrashEmptying] = useState(false);

  // Trash callbacks - will be passed to TrashView
  const [trashCallbacks, setTrashCallbacks] = useState<{
    onSelectAll?: () => void;
    onClearSelection?: () => void;
    onRestore?: () => void;
    onEmptyTrash?: () => void;
    onEmptySelected?: () => void;
  }>({});

  if (!user) return null;

  const isAdmin = user.role === 'admin';
  const hasSubscriptionIssueFlag = hasSubscriptionIssue();

  // Extract menu bar for UnifiedHeader
  const profileMenuBar = (
    <ProfileMenuBar
      activeTab={activeTab}
      onTabChange={(tab) => {
        setActiveTab(tab);
        localStorage.setItem('profile-activeTab', tab);
      }}
      isAdmin={isAdmin}
      hasSubscriptionIssue={hasSubscriptionIssueFlag}
      trashFilter={trashFilter}
      onTrashFilterChange={setTrashFilter}
      trashSelectedCount={trashSelectedCount}
      trashTotalCount={trashTotalCount}
      onTrashSelectAll={trashCallbacks.onSelectAll}
      onTrashClearSelection={trashCallbacks.onClearSelection}
      onTrashRestore={trashCallbacks.onRestore}
      onTrashEmptyTrash={trashCallbacks.onEmptyTrash}
      onTrashEmptySelected={trashCallbacks.onEmptySelected}
      trashRestoring={trashRestoring}
      trashEmptying={trashEmptying}
    />
  );

  // Dynamic subtitle based on active tab
  const getProfileSubtitle = () => {
    switch (activeTab) {
      case 'account':
        return 'Account settings';
      case 'subscription':
        return 'Manage subscription';
      case 'trash':
        return 'Deleted items';
      case 'admin':
        return 'Admin dashboard';
      case 'users':
        return 'User management';
      case 'stats':
        return isAdmin ? 'System statistics' : 'Your statistics';
      case 'queue':
        return 'Processing queue';
      default:
        return 'Profile';
    }
  };

  return (
    <>
      {isActive && (
        <UnifiedHeader 
          title="Profile"
          subtitle={getProfileSubtitle()}
          menuBar={profileMenuBar}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
        />
      )}
      <PageTemplate
        pageTitle=""
        pageSummary=""
        showPageHeader={false}
        showInputArea={false}
        enableSmartScroll={false}
        stickyInput={true}
        className="profile-page"
      >
      {activeTab === 'account' && <AccountTab onShowQuickStartGuide={onShowQuickStartGuide} />}
      {activeTab === 'subscription' && <SubscriptionTab onViewPlans={onViewPlans} />}
      {activeTab === 'trash' && (
        <TrashView 
          onTrashCountChange={onTrashCountChange}
          filter={trashFilter}
          onFilterChange={setTrashFilter}
          onStateChange={(state) => {
            setTrashSelectedCount(state.selectedCount);
            setTrashTotalCount(state.totalCount);
            setTrashRestoring(state.restoring);
            setTrashEmptying(state.emptying);
          }}
          onCallbacksReady={setTrashCallbacks}
        />
      )}
      {activeTab === 'admin' && isAdmin && <AdminTab />}
      {activeTab === 'users' && isAdmin && <UsersTab />}
      {activeTab === 'stats' && (isAdmin ? <SystemStatsTab /> : <UserStatsTab />)}
      {activeTab === 'queue' && isAdmin && <QueueTab />}
    </PageTemplate>
    </>
  );
};

export default ProfileView;
